import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { recordConsoleError } from '../utils/diagnosticsBuffer';

// Sep 2026 · Structural guarantee against "URL changed but the page didn't".
// The address bar is updated synchronously by history.pushState while the
// rendered route comes from React state. If those two ever disagree for
// longer than STUCK_MS the render is wedged (suspended chunk, stuck
// transition, crashed effect) - we hard-navigate to the URL the user asked
// for so they always land on the page they clicked.
const STUCK_MS = 2500;
const RELOAD_KEY = 'akf_nav_watchdog_reload_at';

// Pathname only: pages legitimately strip query params via history.replaceState.
const currentHref = () => window.location.pathname;

export default function NavigationWatchdog() {
  const location = useLocation();
  const rendered = useRef(location.pathname);
  useEffect(() => { rendered.current = location.pathname; }, [location.pathname]);

  useEffect(() => {
    let pending = false;
    const tick = () => {
      const actual = currentHref();
      if (pending || actual === rendered.current) return;
      pending = true;
      // Re-check after the grace period: still divergent = wedged.
      setTimeout(() => {
        pending = false;
        if (currentHref() !== actual || actual === rendered.current) return;
        const last = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
        if (Date.now() - last < 15000) return;
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        recordConsoleError(`[nav-watchdog] route render wedged: url=${actual} rendered=${rendered.current} - hard navigating`);
        window.location.assign(window.location.href);
      }, STUCK_MS);
    };
    const onPop = () => tick();
    window.addEventListener('popstate', onPop);
    const id = setInterval(tick, 1000);
    return () => { clearInterval(id); window.removeEventListener('popstate', onPop); };
  }, []);

  return null;
}
